import React, { useState } from 'react';
import { uniqBy } from 'lodash';
import { ApInput, colors } from '@alixpartners/ui-components';
import { getIdealDenomination } from 'utils/number';
import {
	StyledSection as Section,
	MetadataSummary,
	MetadataSummaryLine,
} from '../ExportMetadataSection/styles';
import {
	Note,
	Select,
	SelectWrapper,
	SummaryLabel,
	SummaryValue,
	ValueWrapper,
} from './CompanyMetadata.styles';
import { UNITS } from '../../Settings';

const NOT_SET = 'Not set';

const getOptions = dataOptions => {
	return uniqBy(
		[
			{
				label: NOT_SET,
				value: NOT_SET,
			},
			...(dataOptions || []).map(opt => ({
				label: opt.value,
				value: opt.key,
			})),
		],
		'value'
	);
};

const getUnitOptions = () => [
	{
		label: 'None',
		value: '1',
		worth: 1,
	},
	...UNITS.map(unit => ({
		...unit,
		label: unit.value,
	})),
];

const getValue = obj => {
	if (!obj || !obj.data || !obj.data.key) {
		return null;
	}
	return obj.data.key;
};

const currentYear = new Date().getFullYear();
const getYearRange = () => {
	const years = [NOT_SET];
	// Last 30 years
	for (let i = currentYear - 30; i <= currentYear; i++) {
		years.push(i);
	}
	return years.map(x => {
		return { label: x, value: x };
	});
};

export const CompanyTile = ({
	key,
	companyId,
	companyYear,
	companyAliasName,
	companyName,
	industry,
	revenue,
	onUpdateCompany,
	options,
}) => {
	const allUnitOptions = getUnitOptions();
	const revenueIdealDenomination = revenue ? getIdealDenomination(revenue) : 1;
	const [revenueValue, setRevenueValue] = useState(
		revenue ? revenue / revenueIdealDenomination : ''
	);
	const unitOption = allUnitOptions.find(
		option => option.worth === revenueIdealDenomination
	);
	const [revenueUnit, setRevenueUnit] = useState(unitOption.value);
	const [year, setYear] = useState(companyYear ? companyYear : NOT_SET);

	return (
		<Section animationIndex={1} title={companyName} key={key}>
			<Note>
				<span className="note_heading">Note:</span> Fields marked with a
				<span style={{ color: colors.red600 }}> * </span>are mandatory to fill.
			</Note>
			<MetadataSummary>
				<MetadataSummaryLine>
					<SummaryLabel>Alias Name:</SummaryLabel>
					<SummaryValue>{companyAliasName}</SummaryValue>
				</MetadataSummaryLine>
				<MetadataSummaryLine>
					<SummaryLabel>
						<span style={{ color: colors.red600 }}>*</span>Year:
					</SummaryLabel>
					<SummaryValue>
						<Select
							name={`${companyName}-${companyId}-year`}
							onChange={x => {
								setYear(x);
								onUpdateCompany(companyId, 'year', x);
							}}
							options={getYearRange()}
							value={year}
						/>
					</SummaryValue>
				</MetadataSummaryLine>
				<MetadataSummaryLine>
					<SummaryLabel>
						<span style={{ color: colors.red600 }}>*</span>Industry:
					</SummaryLabel>
					<SummaryValue>
						<Select
							name={`${companyName}-${companyId}-industry`}
							onChange={v => {
								onUpdateCompany(companyId, 'industry', v);
							}}
							options={getOptions(options.industries)}
							value={getValue(industry)}
						/>
					</SummaryValue>
				</MetadataSummaryLine>
				<MetadataSummaryLine>
					<SummaryLabel>
						<span style={{ color: colors.red600 }}>*</span>Revenue:
					</SummaryLabel>
					<SummaryValue>
						<ValueWrapper>
							<ApInput
								type="number"
								name="revenue"
								onChange={v => {
									setRevenueValue(parseFloat(v));
									onUpdateCompany(
										companyId,
										'revenue',
										parseFloat(v) * parseInt(revenueUnit)
									);
								}}
								value={revenueValue}
								shouldStopPropagation
							/>
						</ValueWrapper>
						<SelectWrapper>
							<Select
								name={`${companyName}-${companyId}-revenue`}
								small
								options={allUnitOptions}
								value={revenueUnit}
								onChange={v => {
									const foundOption = allUnitOptions.find(
										option => option.value === v
									);
									setRevenueUnit(foundOption.value);

									onUpdateCompany(
										companyId,
										'revenue',
										parseFloat(revenueValue) * parseInt(foundOption.worth)
									);
								}}
							/>
						</SelectWrapper>
					</SummaryValue>
				</MetadataSummaryLine>
			</MetadataSummary>
		</Section>
	);
};
